import * as React from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
import { MENU_ITEMS } from "./listItems";
import { extractUserInfoFromToken, getLocalStorageSAMLToken } from "../utils";
import { accessRequest } from "../redux/dynamo_db/actions";
import { useDispatch } from "react-redux";
import getUserProfiles from "../tokenUtils";

export default function Victoria() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profiles = getUserProfiles() || [];

  const locationObject = {
    materials: "Materials",
    placeholdermaterials: "Placeholder Materials",
    systems: "Systems",
    systemapplications: "System Applications",
    licenses: "Licenses",
    contracts: "Contracts",
    orders: "Orders",
    serialnumbers: "Serial Numbers",
    dataqualitysaporders: "Data Quality Sap Orders",
    hwsprocess: "Hardware Support Process - Asset Data transfer",
    missingsnsstatus: "Missing Serial Numbers Status",
    sfdcsynchronizermonitoring: "SFDC Synchronizer Monitoring",
    athena: "Athena Query",
    bulkreport: "Bulk Report",
    upload: "Upload Documents",
    monitoring: "Monitoring",
    clienterrors: "Client Errors",
    unprocesseditems: "Unprocessed Items",
    // Only discoverable by My Portal-entitled users (the route is restricted too).
    ...(profiles.includes("vic-qa-my-portal") ||
    profiles.includes("vic-prod-my-portal")
      ? { myportal: "My Portal" }
      : {}),
  };
  const locationHelpText = {
    unprocesseditems: (
      <div>
        <p>
          Admin-only. Lists raw rows that failed to be created in the datalake
          (a serial number, OV license, order or system) along with their type,
          unique id, and failure reason, so support can inspect and request a
          resend without touching AWS directly.
        </p>
        <p>
          Search by type / error reason or a known unique id, export the result
          in a format ready to reimport, and — for admins — edit and resend a
          row so data-pipelines replays it through its normal ingestion
          pipeline.
        </p>
      </div>
    ),
    myportal: (
      <div>
        <p>
          Query the three My Portal DynamoDB tables (objects, events-raw,
          analytics) through their indexes and GSIs, aggregate object analytics,
          and map static page paths to human-readable aliases.
        </p>
      </div>
    ),
    clienterrors: (
      <div>
        <p>
          Admin-only. Lists front-end blocking issues automatically reported by
          the app: requests that timed out with no response, failed at the
          network/CORS layer, or returned a server error.
        </p>
        <p>
          Each entry includes the user, page, resource, HTTP status, duration
          and full browser context — so a stuck "loading" spinner can be
          diagnosed without asking the user to open DevTools.
        </p>
      </div>
    ),
    materials: (
      <div>
        <p>
          This page allows you to search for Materials with different
          filter.{" "}
        </p>
        <p>
          A Material in an asset with a serial number. It can be a DATA or a
          VOICE material if coming from SAP or ARUBA material if coming from
          LDS.
        </p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    contracts: (
      <div>
        <p>This page allows you to search for Contracts. </p>
        <p>
          Contracts can be DATA or VOICE contracts from SAP, or automatic
          contracts created in the datalake. It can also be subscriptions
          contracts from ZUORA.
        </p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    orders: (
      <div>
        <p>This page allows you to search for SAP Orders. </p>
        <p>Those data are RAW data received in the datalake from SAP.</p>
      </div>
    ),
    placeholdermaterials: (
      <div>
        <p>This page allows you to search for Placeholder Materials. </p>
        <p>
          A Placeholder Material was a material that undergo a change of SAP
          order.
        </p>
        <p>
          We keep in this object all the information from before the order
          change and all of the paid contract. We don't keep automatic contracts
          like Standard Hardware Support.
        </p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    systems: (
      <div>
        <p>This page allows you to search for Systems. </p>
        <p>A System is an Asset voice from ELP.</p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    licenses: (
      <div>
        <p>This page allows you to search for Licenses. </p>
        <p>A Licenses is an OV2500 Asset from LDS.</p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    systemapplications: (
      <div>
        <p>This page allows you to search for System Applications. </p>
        <p>A System Application is an opex Asset voice from ELP.</p>
        <p>
          The results in the detail page are elements from aggregated sources.
        </p>
      </div>
    ),
    serialnumbers: (
      <div>
        <p>This page allows you to search for Serial numbers. </p>
        <p>Those data are data received in the datalake from SAP or LDS.</p>
        <p>They are already processed data before asset creation.</p>
      </div>
    ),
    dataqualitysaporders: (
      <div>
        <p>
          This page allows you to search for SAP Order data quality analysis.
        </p>
        <p>
          The elements in this page are SAP Order lines that was analysis in an
          automatic process to determine if there was any missing serial number.
        </p>
        <p>
          All the elements display are lines that was once incomplete in the
          analysis.
        </p>
      </div>
    ),
    hwsprocess: (
      <div>
        <p>
          This page allows you to search for Serial number which went through
          Hardware support process.{" "}
        </p>
        <p>
          The elements display are all serial numbers that was send back to ALE
          with a hardware support Order and process by the datalake.
        </p>
      </div>
    ),
    missingsnsstatus: (
      <div>
        <p>
          This page allows you to search for Serial number which was saw has
          missing at one moment by the datalake.
        </p>
      </div>
    ),
    sfdcsynchronizermonitoring: (
      <div>
        <p>
          This page allows you to monitor the SFDC Synchronizer jobs and their
          status.
        </p>
        <p>
          You can search by element ID, detail type or source and trigger a
          re-synchronization for asset and contract entries.
        </p>
      </div>
    ),
    athena: (
      <div>
        <p>
          This page lets you query the datalake with Athena over the Glue
          tables.
        </p>
        <p>
          Build a query with the guided builder (database, table, columns,
          filters) or, for admins, write raw read-only SQL. Results preview the
          first 100 rows and the full result set can be downloaded.
        </p>
      </div>
    ),
    bulkreport: (
      <div>
        <p>
          This page allows you to see all your bulk upload request and download
          the result.
        </p>
      </div>
    ),
    upload: (
      <div>
        <p>
          This page allows you to upload data for different uses cases directly
          in S3.
        </p>
      </div>
    ),
    monitoring: (
      <div>
        <p>This page show the page usage by day/month/lifetime</p>
      </div>
    ),
  };

  const handleNavigate = (path) => {
    navigate(`/${path}`);
  };

  const handleRequestAccess = () => {
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    let queryObject = {};
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(accessRequest(queryObject));
  };

  return (
    <React.Fragment>
      <Grid container spacing={3}>
        {Object.entries(locationObject).map(([key, label]) => (
          <Grid item xs={12} sm={6} lg={4} key={key}>
            <Card sx={{ height: "100%" }}>
              <CardActionArea
                onClick={() => handleNavigate(key)}
                sx={{ height: "100%", alignItems: "flex-start" }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "primary.main",
                        bgcolor: "primary.lighter",
                        flexShrink: 0,
                      }}
                    >
                      <MaterialSymbol
                        name={
                          MENU_ITEMS.find((item) => item.url === `/${key}`)
                            ?.icon || "chevron_right"
                        }
                        size="kpi"
                      />
                    </Box>
                    <Typography variant="h4" component="h2">
                      {label}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    sx={{ "& p": { m: 0, mb: 1 }, "& p:last-child": { mb: 0 } }}
                  >
                    {locationHelpText[key] || "No description available."}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mt: 5,
          p: 3,
          textAlign: "center",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body1" gutterBottom>
          If you can only access this page, click on the button below and an
          admin will look at the request.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRequestAccess}
          sx={{ mt: 1 }}
        >
          Request Access
        </Button>
      </Box>
    </React.Fragment>
  );
}
